import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { decryptData, projectURL, serverLink } from "../../../resources/url";
import DataLoader from "../../common/dataLoader/dataLoader";
import { currencyConverter, formatDateAndTime } from "../../../resources/constants";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router";
import DataTable from "../../common/data-table/data-table";

const Field = ({ label, value, colClass = "col-6 col-md-4 col-xl-3" }) => (
    <div className={colClass}>
        <div className="p-3 rounded border h-100" style={{ background: "var(--tblr-bg-surface-secondary, #f8fafc)" }}>
            <div className="text-uppercase fw-semibold text-muted mb-1"
                 style={{ fontSize: "0.68rem", letterSpacing: "0.06em" }}>
                {label}
            </div>
            <div className="fw-medium" style={{ fontSize: "0.92rem" }}>
                {value ?? <span className="text-muted">—</span>}
            </div>
        </div>
    </div>
);

const SectionHeader = ({ title }) => (
    <div className="col-12">
        <div className="d-flex align-items-center gap-2 mt-1 mb-3">
            <div className="flex-fill border-top" />
            <span className="text-muted small fw-semibold text-uppercase px-2"
                  style={{ letterSpacing: "0.07em", whiteSpace: "nowrap", fontSize: "0.72rem" }}>
                {title}
            </span>
            <div className="flex-fill border-top" />
        </div>
    </div>
);

function StudentDashboard(props) {
    const navigate = useNavigate();
    const { slug } = useParams();

    if (!slug) navigate("/");

    const [isLoading, setIsLoading] = useState(true);
    const [studentData, setStudentData] = useState(null);
    const [courseList, setCourseList] = useState([]);
    const [paymentList, setPaymentList] = useState([]);
    const [courseData, setCourseData] = useState([]);

    const courseHeader = ["S/N", "Course Code", "Course Title", "Level", "Semester", "Date Registered"];
    const paymentHeader = ["S/N", "Payment Type", "Amount", "Transaction ID", "Transaction Date", "Receipt"];

    // Group courses by level + semester for summary cards
    const courseSummary = useMemo(() => {
        const groups = {};
        courseList.forEach((c) => {
            const key = `${c.LEVEL}-${c.SEMESTER}`;
            if (!groups[key]) {
                groups[key] = { level: c.LEVEL, semester: c.SEMESTER, count: 0 };
            }
            groups[key].count++;
        });
        return Object.values(groups).sort((a, b) =>
            a.level !== b.level ? Number(a.level) - Number(b.level) : Number(a.semester) - Number(b.semester)
        );
    }, [courseList]);

    const totalPayments = useMemo(
        () => paymentList.reduce((sum, p) => sum + (Number(p.AMOUNT) || 0), 0),
        [paymentList]
    );

    useEffect(() => {
        getData();
    }, [slug]);

    const getData = async () => {
        try {
            const { data } = await axios.get(
                `${serverLink}staff/report/student/dashboard/data/${decryptData(slug)}`
            );
            if (data.Biodata?.length > 0) {
                setStudentData({
                    personal: data.Biodata[0],
                    contact: data.Contact?.[0] ?? null,
                    olevel: data.Olevel?.[0] ?? null,
                    nok: data.NOK?.[0] ?? null,
                    qualification: data.Qualification?.[0] ?? null,
                });
                setCourseList(data.Courses ?? []);
                setPaymentList(data.Payments ?? []);
                setCourseData(data.CourseData ?? []);
            }
        } catch (err) {
            console.error("Network error loading student dashboard", err);
        } finally {
            setIsLoading(false);
        }
    };

    const getCourseTitle = (courseId) => {
        const found = courseData.find((c) => c.COURSE_ID?.toString() === courseId?.toString());
        return found?.COURSE_TITLE ?? "—";
    };

    const semesterLabel = (s) => (s?.toString() === "1" ? "First" : "Second");

    const showCourseTable = () =>
        courseList.map((item, index) => (
            <tr key={index}>
                <td className="text-muted w-1">{index + 1}</td>
                <td>
                    <span className="badge bg-blue-lt fw-medium">{item.COURSE_CODE}</span>
                </td>
                <td>{getCourseTitle(item.COURSEID)}</td>
                <td>
                    <span className="badge bg-purple-lt">Level {item.LEVEL}00</span>
                </td>
                <td>
                    <span className="badge bg-teal-lt">{semesterLabel(item.SEMESTER)} Semester</span>
                </td>
                <td className="text-muted">{formatDateAndTime(item.DATE_REG, "date")}</td>
            </tr>
        ));

    const showPaymentTable = () =>
        paymentList.map((item, index) => (
            <tr key={index}>
                <td className="text-muted w-1">{index + 1}</td>
                <td>{item.PAYMENT_TYPE}</td>
                <td className="fw-medium text-green">{currencyConverter(item.AMOUNT)}</td>
                <td>
                    <code className="text-muted">{item.TRANSACTION_ID}</code>
                </td>
                <td className="text-muted">
                    {item.DATE_PAID ? formatDateAndTime(item.DATE_PAID, "date") : "—"}
                </td>
                <td>
                    <button type="button" className="btn btn-sm btn-ghost-primary d-inline-flex align-items-center gap-1">
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-sm" width="16" height="16"
                             viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none"
                             strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                            <path d="M14 3v4a1 1 0 0 0 1 1h4"/>
                            <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z"/>
                            <line x1="12" y1="11" x2="12" y2="17"/>
                            <polyline points="9 14 12 17 15 14"/>
                        </svg>
                        Receipt
                    </button>
                </td>
            </tr>
        ));

    if (isLoading) {
        return (
            <div className="container-xl d-flex align-items-center justify-content-center" style={{ minHeight: "60vh" }}>
                <DataLoader />
            </div>
        );
    }

    const p = studentData?.personal;
    const c = studentData?.contact;
    const nok = studentData?.nok;
    const olevel = studentData?.olevel;
    const qual = studentData?.qualification;
    const isActive = p?.IS_ACTIVE?.toString() === "1";

    return (
        <>
            <div className="container-xl">
                <div className="page-header d-print-none">
                    <div className="row align-items-center">
                        <div className="col-auto">
                            <button
                                className="btn btn-ghost-secondary btn-sm d-flex align-items-center gap-1"
                                onClick={() => navigate(-1)}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-sm" width="24" height="24"
                                     viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none"
                                     strokeLinecap="round" strokeLinejoin="round">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                    <line x1="5" y1="12" x2="19" y2="12"/>
                                    <line x1="5" y1="12" x2="11" y2="18"/>
                                    <line x1="5" y1="12" x2="11" y2="6"/>
                                </svg>
                                Back
                            </button>
                        </div>
                        <div className="col">
                            <div className="page-pretitle">Human Resources / Student Manager</div>
                            <h2 className="page-title">Student Dashboard</h2>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body">
                <div className="container-xl">

                    {/* ── Profile Header ── */}
                    <div className="card mb-3">
                        <div className="card-body">
                            <div className="row align-items-center g-4">
                                <div className="col-auto">
                                    <span
                                        className="avatar"
                                        style={{
                                            backgroundImage: `url(${projectURL}assets/passport/${p?.PICTURE})`,
                                            backgroundSize: "cover",
                                            backgroundPosition: "center",
                                            width: "90px",
                                            height: "110px",
                                            borderRadius: "8px",
                                            border: "3px solid var(--tblr-border-color)",
                                        }}
                                    />
                                </div>
                                <div className="col">
                                    <h2 className="mb-1">
                                        {p?.FIRST_NAME} {p?.MIDDLE_NAME} {p?.LAST_NAME}
                                    </h2>
                                    <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                                        <span className="badge bg-blue-lt">
                                            {p?.STUDENTID || p?.APPID}
                                        </span>
                                        <span className={`badge ${isActive ? "bg-success" : "bg-danger"}`}>
                                            {isActive ? "Active" : "Inactive"}
                                        </span>
                                        {p?.STUDENT_STATUS && (
                                            <span className="badge bg-purple-lt">{p.STUDENT_STATUS}</span>
                                        )}
                                    </div>
                                    <div className="text-muted d-flex align-items-center gap-1">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-sm" width="16" height="16"
                                             viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none"
                                             strokeLinecap="round" strokeLinejoin="round">
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                            <path d="M22 9l-10 -4l-10 4l10 4l10 -4v6"/>
                                            <path d="M6 10.6v5.4a6 3 0 0 0 12 0v-5.4"/>
                                        </svg>
                                        {p?.PROGRAMME}
                                    </div>
                                </div>
                                <div className="col-auto">
                                    <button
                                        className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                                        data-bs-toggle="modal"
                                        data-bs-target="#modal-report"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-sm" width="16" height="16"
                                             viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none"
                                             strokeLinecap="round" strokeLinejoin="round">
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                            <path d="M9 7h-3a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-3"/>
                                            <path d="M9 15h3l8.5 -8.5a1.5 1.5 0 0 0 -3 -3l-8.5 8.5v3"/>
                                        </svg>
                                        Edit Profile
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Quick stats footer */}
                        <div className="card-footer p-0">
                            <div className="row divide-x text-center">
                                <div className="col py-3">
                                    <div className="fw-bold fs-3 text-primary">{courseList.length}</div>
                                    <div className="text-muted small">Courses Registered</div>
                                </div>
                                <div className="col py-3">
                                    <div className="fw-bold fs-3 text-green">{paymentList.length}</div>
                                    <div className="text-muted small">Payments</div>
                                </div>
                                <div className="col py-3">
                                    <div className="fw-bold fs-3 text-purple">
                                        {p?.STUDENT_LEVEL ? `${p.STUDENT_LEVEL}00` : "—"}
                                    </div>
                                    <div className="text-muted small">Current Level</div>
                                </div>
                                <div className="col py-3">
                                    <div className="fw-bold fs-3">{p?.ADMISSION_YEAR || "—"}</div>
                                    <div className="text-muted small">Admission Year</div>
                                </div>
                                <div className="col py-3">
                                    <div className="fw-bold fs-3 text-orange">{currencyConverter(totalPayments)}</div>
                                    <div className="text-muted small">Total Paid</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Tabs Card ── */}
                    <div className="card">
                        {/* Modern pill navigation */}
                        <div className="card-header border-bottom"
                             style={{ background: "var(--tblr-bg-surface-secondary, #f8fafc)" }}>
                            <div className="w-100 overflow-auto pb-1">
                                <ul className="nav nav-pills flex-nowrap" style={{ gap: "4px" }}
                                    data-bs-toggle="tabs">
                                    {[
                                        {
                                            id: "tab-personal", label: "Personal Info", active: true,
                                            icon: <><circle cx="12" cy="7" r="4"/><path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"/></>,
                                        },
                                        {
                                            id: "tab-contact", label: "Contact",
                                            icon: <><path d="M5 4h4l2 5l-2.5 1.5a11 11 0 0 0 5 5l1.5 -2.5l5 2v4a2 2 0 0 1 -2 2a16 16 0 0 1 -15 -15a2 2 0 0 1 2 -2"/></>,
                                        },
                                        {
                                            id: "tab-nok", label: "Next of Kin",
                                            icon: <><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/><path d="M21 21v-2a4 4 0 0 0 -3 -3.85"/></>,
                                        },
                                        {
                                            id: "tab-olevel", label: "O\u2019Level",
                                            icon: <><path d="M14 3v4a1 1 0 0 0 1 1h4"/><path d="M5 8v-3a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2h-5"/><circle cx="6" cy="14" r="3"/><path d="M4.5 17l-1.5 5l3 -1.5l3 1.5l-1.5 -5"/></>,
                                        },
                                        {
                                            id: "tab-alevel", label: "A\u2019Level",
                                            icon: <><path d="M22 9l-10 -4l-10 4l10 4l10 -4v6"/><path d="M6 10.6v5.4a6 3 0 0 0 12 0v-5.4"/></>,
                                        },
                                        {
                                            id: "tab-payments", label: "Payments",
                                            badge: paymentList.length > 0 ? paymentList.length : null,
                                            badgeColor: "bg-green",
                                            icon: <><path d="M7 18v-10.948a1.05 1.05 0 0 1 1.968 -.51l6.064 10.916a1.05 1.05 0 0 0 1.968 -.51v-10.948"/><path d="M5 10h14"/><path d="M5 14h14"/></>,
                                        },
                                        {
                                            id: "tab-courses", label: "Courses",
                                            badge: courseList.length > 0 ? courseList.length : null,
                                            badgeColor: "bg-blue",
                                            icon: <><path d="M3 19a9 9 0 0 1 9 0a9 9 0 0 1 9 0"/><path d="M3 6a9 9 0 0 1 9 0a9 9 0 0 1 9 0"/><line x1="3" y1="6" x2="3" y2="19"/><line x1="12" y1="6" x2="12" y2="19"/><line x1="21" y1="6" x2="21" y2="19"/></>,
                                        },
                                    ].map((tab) => (
                                        <li key={tab.id} className="nav-item">
                                            <a
                                                href={`#${tab.id}`}
                                                data-bs-toggle="tab"
                                                className={`nav-link d-flex align-items-center gap-1 px-3 py-2${tab.active ? " active" : ""}`}
                                                style={{ whiteSpace: "nowrap" }}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                     viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"
                                                     fill="none" strokeLinecap="round" strokeLinejoin="round"
                                                     style={{ flexShrink: 0 }}>
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                                    {tab.icon}
                                                </svg>
                                                {tab.label}
                                                {tab.badge && (
                                                    <span className={`badge ${tab.badgeColor} ms-1`}
                                                          style={{ fontSize: "0.65rem" }}>
                                                        {tab.badge}
                                                    </span>
                                                )}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        <div className="card-body">
                            <div className="tab-content">

                                {/* ── Personal Info ── */}
                                <div className="tab-pane active show" id="tab-personal">
                                    <div className="row g-3">
                                        <SectionHeader title="Identity & Personal" />
                                        <Field label="Gender" value={p?.GENDER} />
                                        <Field label="Date of Birth" value={p?.DOB ? formatDateAndTime(p.DOB, "date") : null} />
                                        <Field label="Blood Group" value={p?.BLOOD_GROUP} />
                                        <Field label="Phone Number" value={p?.TELEPHONE} />
                                        <Field label="Email Address" colClass="col-12 col-md-6 col-xl-6"
                                               value={<span className="text-lowercase">{p?.EMAIL}</span>} />

                                        <SectionHeader title="Academic Information" />
                                        <Field label="Programme" colClass="col-12 col-md-6"
                                               value={p?.PROGRAMME} />
                                        <Field label="Department" colClass="col-12 col-md-6"
                                               value={p?.DEPARTMENT} />
                                        <Field label="Current Level" value={p?.STUDENT_LEVEL ? `${p.STUDENT_LEVEL}00` : null} />
                                        <Field label="Admission Year" value={p?.ADMISSION_YEAR} />
                                        <Field label="Admission Batch" value={p?.ADMISSION_BATCH} />

                                        <SectionHeader title="Enrolment Status" />
                                        <Field label="Application Status" value={
                                            p?.APP_STATUS ? (
                                                <span className={`badge ${p.APP_STATUS === "Active" ? "bg-success-lt text-success" : "bg-warning-lt text-warning"}`}>
                                                    <span className={`status-dot me-1 ${p.APP_STATUS === "Active" ? "status-green" : "status-yellow"}`} />
                                                    {p.APP_STATUS}
                                                </span>
                                            ) : null
                                        } />
                                        <Field label="Clearance Status" value={
                                            p?.CLEARANCE_STATUS?.toString() === "1" ? (
                                                <span className="badge bg-success-lt text-success">
                                                    <span className="status-dot status-green me-1" />
                                                    Cleared
                                                </span>
                                            ) : (
                                                <span className="badge bg-warning-lt text-warning">
                                                    <span className="status-dot status-yellow me-1" />
                                                    Pending
                                                </span>
                                            )
                                        } />
                                        <Field label="Account Status" value={
                                            isActive ? (
                                                <span className="badge bg-success-lt text-success">
                                                    <span className="status-dot status-green me-1" />
                                                    Active
                                                </span>
                                            ) : (
                                                <span className="badge bg-danger-lt text-danger">
                                                    <span className="status-dot status-red me-1" />
                                                    Inactive
                                                </span>
                                            )
                                        } />
                                    </div>
                                </div>

                                {/* ── Contact ── */}
                                <div className="tab-pane" id="tab-contact">
                                    {!c?.STATE ? (
                                        <div className="empty py-5">
                                            <div className="empty-icon">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-lg text-muted"
                                                     width="48" height="48" viewBox="0 0 24 24" strokeWidth="1.5"
                                                     stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                                    <path d="M9 11a3 3 0 1 0 6 0a3 3 0 0 0 -6 0"/>
                                                    <path d="M17.657 16.657l-4.243 4.243a2 2 0 0 1 -2.827 0l-4.244 -4.243a8 8 0 1 1 11.314 0z"/>
                                                </svg>
                                            </div>
                                            <p className="empty-title">No contact record found</p>
                                            <p className="empty-subtitle text-muted">Contact information has not been provided.</p>
                                        </div>
                                    ) : (
                                        <div className="row g-3">
                                            <SectionHeader title="Location & Address" />
                                            <Field label="Nationality" value={c?.NATIONALITY} />
                                            <Field label="State of Origin" value={c?.STATE} />
                                            <Field label="Local Government Area" value={c?.LGA} />
                                            <Field label="Home Address" colClass="col-12"
                                                   value={
                                                       <div className="d-flex align-items-start gap-2">
                                                           <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-sm text-muted mt-1 flex-shrink-0"
                                                                width="16" height="16" viewBox="0 0 24 24" strokeWidth="2"
                                                                stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                               <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                                               <path d="M9 11a3 3 0 1 0 6 0a3 3 0 0 0 -6 0"/>
                                                               <path d="M17.657 16.657l-4.243 4.243a2 2 0 0 1 -2.827 0l-4.244 -4.243a8 8 0 1 1 11.314 0z"/>
                                                           </svg>
                                                           <span>{c?.ADDRESS}</span>
                                                       </div>
                                                   } />
                                        </div>
                                    )}
                                </div>

                                {/* ── Next of Kin ── */}
                                <div className="tab-pane" id="tab-nok">
                                    {!nok?.FIRST_NAME ? (
                                        <div className="empty py-5">
                                            <div className="empty-icon">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-lg text-muted"
                                                     width="48" height="48" viewBox="0 0 24 24" strokeWidth="1.5"
                                                     stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                                    <circle cx="9" cy="7" r="4"/>
                                                    <path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"/>
                                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                                    <path d="M21 21v-2a4 4 0 0 0 -3 -3.85"/>
                                                </svg>
                                            </div>
                                            <p className="empty-title">No next of kin record found</p>
                                            <p className="empty-subtitle text-muted">Next of kin information has not been provided.</p>
                                        </div>
                                    ) : (
                                        <div className="row g-3">
                                            <div className="col-12">
                                                <div className="d-flex align-items-center gap-3 p-3 rounded border mb-1"
                                                     style={{ background: "var(--tblr-bg-surface-secondary, #f8fafc)" }}>
                                                    <span className="avatar avatar-md bg-blue text-white rounded-circle fw-bold"
                                                          style={{ fontSize: "1rem" }}>
                                                        {nok?.FIRST_NAME?.[0]}{nok?.LAST_NAME?.[0]}
                                                    </span>
                                                    <div>
                                                        <div className="fw-semibold">{nok?.FIRST_NAME} {nok?.LAST_NAME}</div>
                                                        <div className="text-muted small">{nok?.EMAIL}</div>
                                                    </div>
                                                </div>
                                            </div>
                                            <SectionHeader title="Contact Details" />
                                            <Field label="First Name" value={nok?.FIRST_NAME} />
                                            <Field label="Last Name" value={nok?.LAST_NAME} />
                                            <Field label="Phone Number"
                                                   value={
                                                       <div className="d-flex align-items-center gap-1">
                                                           <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                                                                className="text-muted" viewBox="0 0 24 24" strokeWidth="2"
                                                                stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                               <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                                               <path d="M5 4h4l2 5l-2.5 1.5a11 11 0 0 0 5 5l1.5 -2.5l5 2v4a2 2 0 0 1 -2 2a16 16 0 0 1 -15 -15a2 2 0 0 1 2 -2"/>
                                                           </svg>
                                                           {nok?.PHONE}
                                                       </div>
                                                   } />
                                            <Field label="Email Address" colClass="col-12 col-md-6"
                                                   value={<span className="text-lowercase">{nok?.EMAIL}</span>} />
                                            <Field label="Address" colClass="col-12"
                                                   value={nok?.ADDRESS} />
                                        </div>
                                    )}
                                </div>

                                {/* ── O'Level ── */}
                                <div className="tab-pane" id="tab-olevel">
                                    {!olevel?.Exam_Number ? (
                                        <div className="empty py-5">
                                            <div className="empty-icon">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-lg text-muted"
                                                     width="48" height="48" viewBox="0 0 24 24" strokeWidth="1.5"
                                                     stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                                    <path d="M14 3v4a1 1 0 0 0 1 1h4"/>
                                                    <path d="M5 8v-3a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2h-5"/>
                                                    <circle cx="6" cy="14" r="3"/>
                                                    <path d="M4.5 17l-1.5 5l3 -1.5l3 1.5l-1.5 -5"/>
                                                </svg>
                                            </div>
                                            <p className="empty-title">No O&apos;Level record found</p>
                                            <p className="empty-subtitle text-muted">O&apos;Level examination details have not been provided.</p>
                                        </div>
                                    ) : (
                                        <div className="row g-3">
                                            <SectionHeader title="Examination Details" />
                                            <Field label="Exam Number" value={olevel?.Exam_Number} />
                                            <Field label="Exam Type" value={olevel?.Exam_Type} />
                                            <Field label="Exam Year" value={olevel?.Exam_Year} />
                                            <Field label="Number of Sittings"
                                                   value={
                                                       <span className="badge bg-blue-lt text-blue px-2 py-1">
                                                           {olevel?.Sittings} sitting{olevel?.Sittings > 1 ? "s" : ""}
                                                       </span>
                                                   } />

                                            <SectionHeader title="Subjects" />
                                            <div className="col-12">
                                                <div className="row g-2">
                                                    {[
                                                        olevel?.Subject1, olevel?.Subject2, olevel?.Subject3,
                                                        olevel?.Subject4, olevel?.Subject5, olevel?.Subject6,
                                                        olevel?.Subject7, olevel?.Subject8, olevel?.Subject9,
                                                    ]
                                                        .filter(Boolean)
                                                        .map((sub, i) => (
                                                            <div key={i} className="col-6 col-md-4 col-xl-3">
                                                                <div className="d-flex align-items-center gap-2 p-2 rounded border"
                                                                     style={{ background: "var(--tblr-bg-surface-secondary, #f8fafc)" }}>
                                                                    <span className="badge bg-blue rounded-circle d-flex align-items-center justify-content-center"
                                                                          style={{ width: "22px", height: "22px", fontSize: "0.65rem", flexShrink: 0 }}>
                                                                        {i + 1}
                                                                    </span>
                                                                    <span className="fw-medium small">{sub}</span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* ── A'Level ── */}
                                <div className="tab-pane" id="tab-alevel">
                                    {!qual?.INSTITUTION ? (
                                        <div className="empty py-5">
                                            <div className="empty-icon">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-lg text-muted"
                                                     width="48" height="48" viewBox="0 0 24 24" strokeWidth="1.5"
                                                     stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                                    <path d="M22 9l-10 -4l-10 4l10 4l10 -4v6"/>
                                                    <path d="M6 10.6v5.4a6 3 0 0 0 12 0v-5.4"/>
                                                </svg>
                                            </div>
                                            <p className="empty-title">No qualification record found</p>
                                            <p className="empty-subtitle text-muted">Higher qualification details have not been provided.</p>
                                        </div>
                                    ) : (
                                        <div className="row g-3">
                                            <div className="col-12">
                                                <div className="p-3 rounded border d-flex align-items-center gap-3"
                                                     style={{ background: "var(--tblr-bg-surface-secondary, #f8fafc)" }}>
                                                    <span className="avatar avatar-md bg-purple text-white rounded-circle">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                                             viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"
                                                             fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                                            <path d="M22 9l-10 -4l-10 4l10 4l10 -4v6"/>
                                                            <path d="M6 10.6v5.4a6 3 0 0 0 12 0v-5.4"/>
                                                        </svg>
                                                    </span>
                                                    <div>
                                                        <div className="fw-semibold">{qual?.INSTITUTION}</div>
                                                        <div className="text-muted small">{qual?.PROGRAMME} · {qual?.QUALIFICATION}</div>
                                                    </div>
                                                    {qual?.YEAR && (
                                                        <span className="ms-auto badge bg-purple-lt">{qual?.YEAR}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <SectionHeader title="Qualification Details" />
                                            <Field label="Institution" colClass="col-12 col-md-6" value={qual?.INSTITUTION} />
                                            <Field label="Programme" colClass="col-12 col-md-6" value={qual?.PROGRAMME} />
                                            <Field label="Qualification" value={qual?.QUALIFICATION} />
                                            <Field label="Class of Degree" value={qual?.CLASS_OF_DEGREE} />
                                            <Field label="CGPA" value={qual?.CGPA} />
                                            <Field label="Year Obtained" value={qual?.YEAR} />
                                        </div>
                                    )}
                                </div>

                                {/* ── Payments ── */}
                                <div className="tab-pane" id="tab-payments">
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <h4 className="mb-0">Payment History</h4>
                                        <div className="d-flex gap-2">
                                            <span className="badge bg-green-lt px-3 py-2">
                                                {paymentList.length} transaction{paymentList.length !== 1 ? "s" : ""}
                                            </span>
                                            <span className="badge bg-blue-lt px-3 py-2 fw-semibold">
                                                Total: {currencyConverter(totalPayments)}
                                            </span>
                                        </div>
                                    </div>
                                    {paymentList.length === 0 ? (
                                        <div className="empty py-4">
                                            <p className="empty-title">No payments found</p>
                                        </div>
                                    ) : (
                                        <DataTable
                                            tableID="payment-history-data"
                                            header={paymentHeader}
                                            body={showPaymentTable()}
                                            title="Payment History"
                                        />
                                    )}
                                </div>

                                {/* ── Courses ── */}
                                <div className="tab-pane" id="tab-courses">
                                    {/* Summary cards per level/semester */}
                                    {courseSummary.length > 0 && (
                                        <>
                                            <div className="d-flex align-items-center gap-2 mb-3">
                                                <h4 className="mb-0">Course Registration Summary</h4>
                                                <span className="badge bg-blue">{courseList.length} total</span>
                                            </div>
                                            <div className="row g-2 mb-4">
                                                {courseSummary.map((grp, i) => (
                                                    <div key={i} className="col-6 col-md-3 col-xl-2">
                                                        <div className="card card-sm text-center border">
                                                            <div className="card-body py-3">
                                                                <div className="text-muted small mb-1">
                                                                    Level {grp.level}00
                                                                </div>
                                                                <div className="text-muted small mb-2">
                                                                    <span className="badge bg-teal-lt">
                                                                        {semesterLabel(grp.semester)} Sem
                                                                    </span>
                                                                </div>
                                                                <div className="display-6 fw-bold text-primary mb-0">
                                                                    {grp.count}
                                                                </div>
                                                                <div className="text-muted small">
                                                                    {grp.count === 1 ? "course" : "courses"}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="hr-text hr-text-left mb-3">
                                                <span className="fw-semibold text-muted">All Registered Courses</span>
                                            </div>
                                        </>
                                    )}

                                    {courseList.length === 0 ? (
                                        <div className="empty py-4">
                                            <p className="empty-title">No courses registered</p>
                                            <p className="empty-subtitle text-muted">
                                                This student has not registered any courses yet.
                                            </p>
                                        </div>
                                    ) : (
                                        <DataTable
                                            tableID="registered-courses"
                                            header={courseHeader}
                                            body={showCourseTable()}
                                            title="Registered Courses"
                                        />
                                    )}
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

const mapStateToProps = (state) => ({
    loginData: state.loginDetails,
});

export default connect(mapStateToProps, null)(StudentDashboard);
